// Builds the "Data Pending List" as Plate table nodes and keeps that block in
// sync with the Data Pending toggle inside the compose-email editor.
//
// Why this exists: previously the toggle only set the `attachDataPendingList`
// flag and the backend appended an invisible table at send time. Inserting the
// table into the editor body makes it visible and editable before sending, and
// it serialises through plateToHtml (which already renders table/tr/td/th with
// emailStyles) into `userProvidedBody` — so the backend flag is no longer needed
// when the in-body table is present.

import type { EntityRequirement } from '../NeedInfoV2/CustomHooks/useFetchRequirements';

// Marker prop stamped on every top-level node this module inserts, so the sync
// can find and strip exactly its own blocks without disturbing what the user
// typed. plateToHtml ignores unknown node props, so it never reaches the email.
export const DATA_PENDING_BLOCK = 'dataPendingBlock' as const;
// Signature of the rendered requirement set, stamped on the inserted table so
// the sync can tell when the content changed (e.g. a GST link resolved after the
// first insert) and rebuild, instead of leaving a stale block in place.
export const DATA_PENDING_SIG = 'dataPendingSig' as const;

export const DATA_PENDING_HEADING = 'Pending Information';
// Column headers + colours mirror the AicaNeedInfoMail email template (the list
// that "used to come" before the in-body table) so the two stay visually in sync.
const COL_DATA_TYPE = 'Data Type';
const COL_DETAILS = 'List of Data and Clarifications';
const COL_STATUS = 'Status';
const EMPTY_CELL = '—';
const DATA_PENDING_COL_SIZES = [180, 330, 140];

// GST-connect rows carry this token in `link` until it is resolved to a real
// magic-link URL upstream (the compose hook). If it still reaches rendering
// unresolved, the surrounding text is shown without a (broken) link.
export const MAGIC_GST_LINK_TOKEN = 'MAGIC_LINK_GST_R1';
const LINK_PLACEHOLDER = '${LINK}';
const LINK_LABEL = 'Link';
// Colours mirror the email template exactly.
const SUBTEXT_COLOR = '#535b62'; // secondary message line
const LINK_COLOR = '#6021b3'; // the appended link (purple)
const STATUS_COMPLETE_COLOR = '#28a745'; // green
const STATUS_PENDING_COLOR = '#e53935'; // red
const REMARK_COLOR = '#9d9d9d'; // remark, muted
const STATUS_COMPLETE_LABEL = 'Completed';
const STATUS_PENDING_LABEL = 'Pending';
const COMPLETE_STATUS = 'COMPLETE';

const paragraph = (text = '', bold = false) => ({
    type: 'p',
    children: [{ text, ...(bold ? { bold: true } : {}) }],
});

const cell = (type: 'td' | 'th', text: string, bold = false) => ({
    type,
    children: [paragraph(text || EMPTY_CELL, bold)],
});

// A cell holding one or more block paragraphs (e.g. primary on line 1, the
// coloured secondary + link on line 2). Empty falls back to the em-dash.
const richCell = (type: 'td' | 'th', paragraphs: any[]) => ({
    type,
    children: paragraphs.length ? paragraphs : [{ type: 'p', children: [{ text: EMPTY_CELL }] }],
});

// Slate inline elements (link nodes) must be flanked by text nodes to serialise
// cleanly, so ensure the children array starts and ends with a text node.
const padInlines = (children: any[]): any[] => {
    if (!children.length) return [{ text: '' }];
    const out = [...children];
    if (out[0]?.type) out.unshift({ text: '' });
    if (out[out.length - 1]?.type) out.push({ text: '' });
    return out;
};

// Build the "List of Data and Clarifications" cell: primaryMessage on the first
// line, then the secondaryMessage (in muted grey) on its own line with the
// ${LINK} placeholder replaced in place by a purple "Link" (the config positions
// it inline, e.g. "(Click here to fetch - ${LINK})"). When the secondary text
// carries no ${LINK} but a link exists, the link is appended after it. Falls back
// to just primaryMessage when there is no secondaryMessage and no resolvable link.
function buildDetailsParagraphs(req: EntityRequirement): any[] {
    const primary = (req?.primaryMessage ?? '').trim();
    const secondary = req?.secondaryMessage?.trim();
    const url = req?.link && req.link !== MAGIC_GST_LINK_TOKEN ? req.link : undefined;
    const linkNode = url ? { type: 'a', url, children: [{ text: LINK_LABEL, color: LINK_COLOR }] } : null;

    const paragraphs: any[] = [{ type: 'p', children: [{ text: primary || EMPTY_CELL }] }];

    // Nothing more to show on a second line.
    if (!secondary && !linkNode) return paragraphs;

    const sub: any[] = [];
    const segments = (secondary ?? '').split(LINK_PLACEHOLDER);
    segments.forEach((segment, i) => {
        if (segment) sub.push({ text: segment, color: SUBTEXT_COLOR });
        // Substitute the link in place wherever ${LINK} appeared.
        if (i < segments.length - 1 && linkNode) sub.push({ ...linkNode });
    });
    // No ${LINK} placeholder but a link exists → append it after the text.
    if (segments.length <= 1 && linkNode) {
        if (secondary) sub.push({ text: ': ', color: SUBTEXT_COLOR });
        sub.push({ ...linkNode });
    }

    paragraphs.push({ type: 'p', children: padInlines(sub) });
    return paragraphs;
}

// Build the "Status" cell: Completed (green) / Pending (red), plus an optional
// muted remark on its own line — same as the email template's status column.
function buildStatusParagraphs(req: EntityRequirement): any[] {
    const isComplete = req?.status === COMPLETE_STATUS;
    const paragraphs: any[] = [
        {
            type: 'p',
            children: [
                {
                    text: isComplete ? STATUS_COMPLETE_LABEL : STATUS_PENDING_LABEL,
                    color: isComplete ? STATUS_COMPLETE_COLOR : STATUS_PENDING_COLOR,
                    bold: true,
                },
            ],
        },
    ];
    const remark = req?.remark?.trim();
    if (remark) paragraphs.push({ type: 'p', children: [{ text: `(${remark})`, color: REMARK_COLOR }] });
    return paragraphs;
}

// Stable signature of what the table renders, so re-syncs can detect content
// changes (e.g. a freshly resolved GST link, or a status change) and rebuild.
function contentSignature(requirements: EntityRequirement[]): string {
    return JSON.stringify(
        (requirements ?? []).map((r) => [
            r?.dataType ?? '',
            r?.primaryMessage ?? '',
            r?.secondaryMessage ?? '',
            r?.link ?? '',
            r?.status ?? '',
            r?.remark ?? '',
        ]),
    );
}

/**
 * Build the tagged Plate nodes for the data-pending list: a bold heading line,
 * a two-column table (Data Type / Details), and a trailing empty paragraph so
 * the caret has a landing spot below the table.
 */
export function buildDataPendingNodes(requirements: EntityRequirement[]): any[] {
    const headerRow = {
        type: 'tr',
        children: [cell('th', COL_DATA_TYPE, true), cell('th', COL_DETAILS, true), cell('th', COL_STATUS, true)],
    };
    // The requirements feed can carry several records that are distinct rows in
    // NeedInfo (different ids) but identical here — same Data Type + Details + Status
    // (e.g. the same bank account requested across multiple GSTINs/batches). The
    // borrower-facing list must show each pending item once, so collapse rows by
    // their rendered content. The id list sent to the backend is unaffected.
    const seen = new Set<string>();
    const uniqueRequirements = (requirements ?? []).filter((req) => {
        const key = JSON.stringify([
            req?.dataType ?? '',
            req?.primaryMessage ?? '',
            req?.secondaryMessage ?? '',
            req?.link ?? '',
            req?.status ?? '',
            req?.remark ?? '',
        ]);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });
    const bodyRows = uniqueRequirements.map((req) => ({
        type: 'tr',
        children: [
            cell('td', req?.dataType ?? '', true),
            richCell('td', buildDetailsParagraphs(req)),
            richCell('td', buildStatusParagraphs(req)),
        ],
    }));

    const table = {
        type: 'table',
        colSizes: DATA_PENDING_COL_SIZES,
        [DATA_PENDING_BLOCK]: true,
        [DATA_PENDING_SIG]: contentSignature(requirements),
        children: [headerRow, ...bodyRows],
    };
    const heading = { ...paragraph(DATA_PENDING_HEADING, true), [DATA_PENDING_BLOCK]: true };
    const trailing = { ...paragraph(''), [DATA_PENDING_BLOCK]: true };

    return [heading, table, trailing];
}

// Pull the still-pending requirements out of a getBorrowerRequirements /
// getLenderRequirements response. Those helpers resolve to a
// { requirements: [...] } object (or `false` on failure). Used by the compose
// modal's normal-flow opens — where no requirement details are carried in from
// the Data Pending button — to populate the same in-body table.
export function extractPendingRequirements(res: any): EntityRequirement[] {
    const list = Array.isArray(res?.requirements) ? res.requirements : [];
    return list.filter((req: EntityRequirement) => req?.status === 'PENDING');
}

// Top-level indices of the nodes carrying our marker. Note this can include
// paragraphs the USER typed after the table: pressing Enter in our trailing
// paragraph makes Slate split it and copy the marker onto the new block. Those
// must be preserved on teardown — see clearOurBlocks.
function taggedIndices(editor: any): number[] {
    const children = editor?.children ?? [];
    const out: number[] = [];
    for (let i = 0; i < children.length; i++) {
        if (children[i]?.[DATA_PENDING_BLOCK]) out.push(i);
    }
    return out;
}

const nodeText = (node: any): string => (node?.children ?? []).map((c: any) => (typeof c?.text === 'string' ? c.text : '')).join('');
const hasElementChild = (node: any): boolean => (node?.children ?? []).some((c: any) => c?.type);
// A paragraph we can safely delete: no inline elements (links) and no text — i.e.
// our trailing spacer, not something the user has written into.
const isEmptyParagraph = (node: any): boolean => node?.type !== 'table' && !hasElementChild(node) && nodeText(node).trim() === '';
const isOurHeading = (node: any): boolean => node?.type !== 'table' && nodeText(node) === DATA_PENDING_HEADING;

// Remove our own blocks at the given indices while PRESERVING anything the user
// authored. Only the table, the heading, and empty spacer paragraphs are ours to
// delete; a marked paragraph that now holds the user's text/links (it inherited
// the marker via an Enter-split) is kept — we just strip the marker so future
// syncs ignore it. Indices are processed high→low so removals don't shift them.
function clearOurBlocks(editor: any, indices: number[]): void {
    for (const i of [...indices].sort((a, b) => b - a)) {
        const node = editor.children?.[i];
        if (!node) continue;
        if (node.type === 'table' || isOurHeading(node) || isEmptyParagraph(node)) {
            editor.tf.removeNodes({ at: [i] });
        } else {
            editor.tf.setNodes({ [DATA_PENDING_BLOCK]: false }, { at: [i] });
        }
    }
}

/**
 * Insert or remove the data-pending block so the editor body matches `enabled`.
 * Idempotent — a no-op when the editor is already in the desired state.
 */
export function syncDataPendingTable(editor: any, enabled: boolean, requirements: EntityRequirement[]): void {
    if (!editor?.tf) return;
    const present = taggedIndices(editor);

    // Tear down when disabled, or when there is nothing to show.
    if (!enabled || !requirements?.length) {
        if (!present.length) return;
        // Slate must always keep at least one child. If every top-level node is
        // ours, drop in a blank paragraph first so removal never empties the doc.
        if (present.length === (editor.children?.length ?? 0)) {
            editor.tf.insertNodes({ type: 'p', children: [{ text: '' }] }, { at: [0] });
        }
        clearOurBlocks(editor, taggedIndices(editor));
        return;
    }

    // Already inserted: rebuild only when the rendered content changed (e.g. a GST
    // magic link resolved after the first insert), otherwise leave the block be so
    // the user's edits survive. Insert the fresh block before clearing the old one
    // (indices captured first; appended nodes don't shift them) so the doc is
    // never momentarily empty, and user-authored content is preserved.
    if (present.length) {
        const tableNode = (editor.children ?? []).find((n: any) => n?.[DATA_PENDING_BLOCK] && n?.type === 'table');
        if (tableNode?.[DATA_PENDING_SIG] === contentSignature(requirements)) return;
        const stale = taggedIndices(editor);
        editor.tf.insertNodes(buildDataPendingNodes(requirements), { at: [editor.children?.length ?? 0] });
        clearOurBlocks(editor, stale);
        return;
    }
    editor.tf.insertNodes(buildDataPendingNodes(requirements), { at: [editor.children?.length ?? 0] });
}
